import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing } = themeVariables;

/* The image's aspect ratio, from the design's 308 x 385 card image. Stated once
   so every slide and the placeholder cannot drift apart. */
const IMAGE_ASPECT_RATIO = '4 / 5';

/**
 * The dots' geometry and colour.
 *
 * All of it is local rather than tokenised, because the theme has no equivalent
 * at this scale: there is no white surface or content token (`stroke.inverse` is
 * a stroke colour and is not white), and no spacing step between `xxs` at 4px and
 * `xs` at 8px for the 6px gap. Same reasoning as the elevation and out-of-stock
 * dimming in `ProductCard.styles.ts`.
 *
 * The design measures the dots at 6 / 5 / 5 / 4 / 3 px left to right. A taper that
 * only shrinks rightward would read as broken the moment the last image is the one
 * on screen, so this treats it as drift and sizes every inactive dot the same.
 */
const DOT_SIZE = '4px';
const DOT_ACTIVE_SIZE = '6px';
const DOT_GAP = '6px';
const DOT_COLOR = 'rgb(255 255 255 / 40%)';
const DOT_ACTIVE_COLOR = 'rgb(255 255 255)';

const HOVER_FADE_MS = '200ms';

/**
 * The images in a row, with no scrolling of any kind.
 *
 * What a card with a single image gets. Nothing here can capture a gesture, so a
 * touch that lands on the image still reaches a horizontally scrollable ancestor,
 * such as a carousel of cards.
 */
export const imageRow = css`
  display: flex;
`;

/**
 * The images, laid out as a horizontal scroller so a swipe moves through them.
 *
 * Only for a card with more than one image: `overflow-x: auto` makes an element a
 * scroll container whether or not its content overflows, and the
 * `overscroll-behavior-x: contain` below then refuses to chain a pan to an ancestor.
 * On a single-image card that combination swallows a swipe meant for a scrollable
 * parent and moves nothing in its place, so those cards use `imageRow` instead.
 *
 * Native scroll-snap rather than a carousel library: a listing renders 60 of these
 * cards, so the swipe should cost the page nothing, and the browser's own momentum
 * and snapping beat anything reimplemented in JavaScript.
 *
 * `overscroll-behavior-x: contain` keeps a swipe past the last image from chaining
 * into the browser's back gesture. The scrollbar is hidden because the dots are the
 * position indicator, and on a trackpad a visible bar would sit over the image.
 */
export const track = css`
  ${imageRow}
  overflow-x: auto;
  /* Explicit, so a sub-pixel rounding on the image's height cannot make the track
     vertically scrollable and swallow a swipe meant for the page. */
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* One image's box: the full width of the row, and the positioning context for the
   tap target stretched over it. */
export const frame = css`
  position: relative;
  flex: 0 0 100%;
`;

/* A frame in the scroller, so exactly one track wide, which is both what makes the
   snap land on whole images and what lets the card read the current index off
   `scrollLeft` alone. `scroll-snap-stop: always` stops a fast flick skipping past
   images. */
export const slide = css`
  ${frame}
  scroll-snap-align: center;
  scroll-snap-stop: always;
`;

export const image = css`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  object-fit: cover;
`;

/* Keeps the card's height stable when a product arrives with no imagery, so a
   row of cards does not jump. */
export const imagePlaceholder = css`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  background-color: var(${colors.surface.disabled});
`;

/**
 * Makes the image open the product.
 *
 * The card's own link stretches over the whole card with a pseudo-element, but it
 * cannot cover the image: it is a sibling of this scroller rather than an ancestor,
 * so a touch landing on it would find nothing scrollable to move. The scroller has
 * to sit on top, and each slide carries its own tap target instead.
 *
 * Empty, `aria-hidden` and unfocusable, so the accessibility tree still holds one
 * link per card named by the title, and the image's own alt text is untouched.
 */
export const slideLink = css`
  position: absolute;
  inset: 0;
`;

/**
 * The second image, revealed while a pointer that can hover is over the image.
 *
 * Desktop gets no swipe — a mouse cannot drag a scroller — so this is how anyone
 * on a mouse sees there is more than one photograph. It is only mounted once the
 * card has been hovered, because rendering a second image on all 60 cards of a
 * listing would double its image requests for a behaviour most visitors, on touch,
 * never trigger. The first hover therefore snaps rather than fading; every one
 * after it cross-fades, since by then the element is already in place.
 */
export const hoverImage = css`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity ${HOVER_FADE_MS};
  pointer-events: none;

  &[data-visible='true'] {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Which image of the set is on screen.
 *
 * Centred across the image and sharing its bottom inset with the favourite button,
 * as the design has it. Decorative: `aria-hidden`, because each slide already
 * announces its position, and `pointer-events: none` so a tap over the dots opens
 * the product rather than landing on nothing.
 *
 * Shown only where the device has a touchscreen, since the dots are what a swipe
 * moves and a swipe is the only thing that visits every image.
 */
export const dots = css`
  position: absolute;
  right: 0;
  bottom: var(${spacing.sm});
  left: 0;
  z-index: 3;
  display: none;
  align-items: center;
  justify-content: center;
  gap: ${DOT_GAP};
  pointer-events: none;

  /* Only a touchscreen swipes, and the dots are the swipe's indicator. A mouse gets
     the hover reveal instead, which only ever reaches the second image, so five dots
     would promise positions the pointer cannot get to.

     Asked as a pointer capability rather than a width, because what decides it is
     whether the device can swipe: a tablet keeps its dots at any size and a desktop
     window loses them at any size. any-pointer rather than pointer for the same
     reason, since pointer describes only the primary input and a touchscreen laptop
     reports that as fine while still swiping perfectly well. */
  @media (any-pointer: coarse) {
    display: flex;
  }
`;

/* One static rule for every dot on the page, with the active state swapped by
   attribute, so a listing of 60 cards injects this once rather than per dot. */
export const dot = css`
  width: ${DOT_SIZE};
  height: ${DOT_SIZE};
  border-radius: 50%;
  background-color: ${DOT_COLOR};

  &[data-active='true'] {
    width: ${DOT_ACTIVE_SIZE};
    height: ${DOT_ACTIVE_SIZE};
    background-color: ${DOT_ACTIVE_COLOR};
  }
`;
